import React, { useState } from "react";
import { EditorTab, TabPosData } from "./types";
import { TabShapeSVG } from "./TabShapeSVG";
import { X, FileCode, FileText, Code2, Cpu } from "lucide-react";
import {OverlayIcon} from "@/components/common/overlay-icon";

interface BrowserTabProps {
  tab: EditorTab;
  index: number;
  isActive: boolean;
  tabWidth: number;
  showClose: boolean;
  pos: TabPosData;
  zIndex: number;
  tabsetId?: string;
  onSelect: (index: number) => void;
  onClose: (index: number) => void;
  onDragStart?: (e: React.DragEvent, index: number) => void;
  onDragOver?: (e: React.DragEvent, index: number) => void;
  onDrop?: (e: React.DragEvent, targetIndex: number) => void;
}

export const BrowserTab: React.FC<BrowserTabProps> = ({
  tab,
  index,
  isActive,
  tabWidth,
  showClose,
  pos,
  zIndex,
  tabsetId,
  onSelect,
  onClose,
  onDragStart,
  onDragOver,
  onDrop,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  // Flutter rule: Non-front stacked depth cards show no content to prevent text & close button clutter
  const showContent = !(pos.isStacked && pos.stackIndex > 0 && !isActive);

  // File extension icon helper: maps extension to SVG icon in public folder
  const getFileIcon = (fileName: string) => {
    const ext = fileName.split('.').pop()?.toLowerCase() || '';

    if (ext === 'tex' || ext === 'cls' || ext === 'sty' || ext === 'bib' || ext === 'latex') {
      return <OverlayIcon src="/im_tex.svg" alt="tex" className="w-4 h-4 object-contain shrink-0" />;
    }
    if (ext === 'pdf') {
      return <img src="/im_pdf.svg" alt="pdf" className="w-4 h-4 object-contain shrink-0" />;
    }
    if (ext === 'js' || ext === 'ts' || ext === 'jsx' || ext === 'tsx') {
      return <img src="/im_js.svg" alt="js" className="w-4 h-4 object-contain shrink-0" />;
    }
    if (ext === 'json') {
      return <img src="/im_json.svg" alt="json" className="w-4 h-4 object-contain shrink-0" />;
    }
    if (ext === 'css') {
      return <img src="/im_css.svg" alt="css" className="w-4 h-4 object-contain shrink-0" />;
    }
    if (ext === 'html') {
      return <img src="/im_html.svg" alt="html" className="w-4 h-4 object-contain shrink-0" />;
    }
    if (ext === 'java') {
      return <img src="/im_java.svg" alt="java" className="w-4 h-4 object-contain shrink-0" />;
    }
    if (ext === 'txt' || ext === 'md') {
      return <img src="/im_txt.svg" alt="txt" className="w-4 h-4 object-contain shrink-0" />;
    }
    if (ext === 'png' || ext === 'jpg' || ext === 'jpeg') {
      return <img src="/im_png.svg" alt="img" className="w-4 h-4 object-contain shrink-0" />;
    }
    if (ext === 'svg') {
      return <img src="/im_svg.svg" alt="svg" className="w-4 h-4 object-contain shrink-0" />;
    }

    return <img src="/im_unsupported_file.svg" alt="file" className="w-4 h-4 object-contain shrink-0" />;
  };

  const rightPad = showClose ? 30 : 12;

  const name = tab.fileName.toLowerCase();
  const shouldShowIcon = !name.endsWith(".pdf") && name !== "console";

  return (
    <div
      style={{
        position: "absolute",
        left: `${pos.x}px`,
        top: "8px",
        width: `${tabWidth}px`,
        height: "44px",
        overflow: "visible", // Allows bottom stroke and bottom drop shadow to render without half-cut clipping
        zIndex: zIndex,
        transition: pos.isStacked ? "none" : "left 0.15s cubic-bezier(0.2, 0, 0, 1)",
      }}
      draggable
      data-editor-tab-index={index}
      data-editor-tab-id={tab.id}
      data-editor-tabset-id={tabsetId}
      onDragStart={(e) => onDragStart?.(e, index)}
      onDragOver={(e) => {
        // Allow bubbling to wrapper for external drag-preview compass
        e.preventDefault(); // Still need this for drop to work
        // Note: don't stopPropagation so dragover bubbles to wrapper's compass handler
        onDragOver?.(e, index);
      }}
      onDrop={(e) => onDrop?.(e, index)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onSelect(index)}
      className="group cursor-pointer select-none"
    >
      {/* SVG Custom Trapezoid Tab Shape */}
      <TabShapeSVG
        width={tabWidth}
        height={44}
        isActive={isActive}
        isHovered={isHovered}
        isStacked={pos.isStacked}
        isLeftStacked={pos.isLeftStacked}
        stackIndex={pos.stackIndex}
        isPdf={tab.fileName.toLowerCase().endsWith('.pdf')}
      />

      {/* Internal Content (Text container with overflow-hidden for clean truncation) */}
      {showContent && (
        <div
          className="relative flex items-center h-[44px] py-1 overflow-hidden"
          style={{
            width: `${tabWidth}px`,
            paddingLeft: "30px",
            paddingRight: `${rightPad}px`,
          }}
        >
          <div className="flex items-center gap-1.5 flex-1 min-w-0 mr-1">
            {shouldShowIcon && getFileIcon(tab.fileName)}
            <span
                className={`truncate text-[13px] tracking-normal font-sans transition-colors ${
                    isActive
                        ? "text-[var(--ed-user-header)] font-semibold"
                        : "text-[var(--ed-browser-tab-text)] font-normal group-hover:text-[var(--ed-user-header)]"
                }`}
            >
  {tab.fileName + (tab.isModified ? " •" : "")}
</span>
          </div>

          {/* Close Tab Button */}
          {showClose && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onClose(index);
              }}
              style={{
                boxShadow: "0.5px 0.5px 1px 0px rgba(0, 0, 0, 0.25)",
                borderColor: "var(--ed-browser-tab-btn-border)",
                borderWidth: "var(--ed-browser-tab-btn-border-width)",
              }}
              className={`w-5 h-5 flex items-center justify-center rounded-[5px] border text-[#555555] hover:bg-slate-100 hover:text-slate-900 transition-all shrink-0 ml-1 ${
                tab.fileName.toLowerCase().endsWith('.pdf') ? "bg-[var(--ed-browser-tab-btn-bg-pdf)]" : "bg-[var(--ed-browser-tab-btn-bg-file)]"
              }`}
              title="Close Tab"
            >
              <X className="w-3 h-3 text-[#555555]" strokeWidth={2.5} />
            </button>
          )}
        </div>
      )}
    </div>
  );
};
